import { Input, Space, Table, Tag, Row } from 'antd';
import React, { useState, useRef } from 'react';
import { SearchOutlined, CheckCircleOutlined, StopOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import localeObjectTable from '../../Utils/localeObjectTable';
import PropTypes from 'prop-types';
import { DatePicker } from 'antd';
import moment from 'moment';
import clienteAxios from '../../Utils/clienteAxios';
import Swal from 'sweetalert2';
import es from 'antd/lib/date-picker/locale/es_ES';
import Boton from '../Atoms/Boton';

const TablaEmpresa = ({ data, setLoading, loading }) => {
  const { RangePicker } = DatePicker;
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const searchInput = useRef(null);
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };
  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };
  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div
        style={{
          padding: '0 8px 8px 8px'
        }}
        onKeyDown={(e) => e.stopPropagation()}>
        <Boton
          type="link"
          size="small"
          onClick={() => {
            close();
          }}
          className="button-close-search-filter-table">
          X
        </Boton>
        <Input
          ref={searchInput}
          placeholder={`Buscar`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: 'block'
          }}
        />
        <Space>
          <Boton
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small">
            Buscar
          </Boton>
          <Boton onClick={() => clearFilters && handleReset(clearFilters)} danger size="small">
            Limpiar
          </Boton>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? '#1890ff' : undefined
        }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: '#ffc069',
            padding: 0
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      )
  });

  const handleDesactivarEmpresa = async (id) => {
    Swal.fire({
      title: 'Desactivar empresa',
      text: '¿Estás seguro de desactivar esta empresa?',
      icon: 'warning',
      reverseButtons: true,
      showCancelButton: true,
      confirmButtonText: '¡Sí, desactivar!',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        clienteAxios
          .post(`/caja-chica/delete`, { id_company: id })
          .then(() => {
            Swal.fire({
              icon: 'success',
              title: '¡Desactivada!',
              text: 'La empresa ha sido desactivada.',
              confirmButtonText: 'Aceptar'
            }).then(() => {
              setLoading(true);
            });
          })
          .catch(() => {
            Swal.fire({
              icon: 'error',
              title: '¡Error!',
              text: 'No se pudo desactivar la empresa.'
            });
          });
      }
    });
  };

  const handleActivarEmpresa = async (id) => {
    Swal.fire({
      title: 'Activar empresa',
      text: '¿Estás seguro de activar esta empresa?',
      icon: 'warning',
      reverseButtons: true,
      showCancelButton: true,
      confirmButtonText: '¡Sí, activar!',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        clienteAxios
          .post(`/caja-chica/company/restore`, { id_company: id })
          .then(() => {
            Swal.fire({
              icon: 'success',
              title: '¡Activada!',
              text: 'La empresa ha sido activada.',
              confirmButtonText: 'Aceptar'
            }).then(() => {
              setLoading(true);
            });
          })
          .catch(() => {
            Swal.fire({
              icon: 'error',
              title: '¡Error!',
              text: 'No se pudo activar la empresa.'
            });
          });
      }
    });
  };

  const tablaReporte = [
    {
      title: 'ID EMPRESA',
      dataIndex: 'id',
      key: 'id',
      ...getColumnSearchProps('id'),
      sorter: (a, b) => a.id - b.id,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'NOMBRE DE LA EMPRESA',
      dataIndex: 'name',
      key: 'name',
      ...getColumnSearchProps('name'),
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'ESTADO DE LA EMPRESA',
      dataIndex: 'status',
      key: 'status',
      filters: [
        {
          text: 'Activada',
          value: '1'
        },
        {
          text: 'Desactivada',
          value: '0'
        }
      ],
      onFilter: (value, record) => record.status === value,
      sorter: (a, b) => a.status.localeCompare(b.status),
      sortDirections: ['ascend', 'descend'],
      render: (text) => {
        switch (text) {
          case '0':
            return <Tag color="error">Desactivada</Tag>;
          case '1':
            return <Tag color="geekblue">Activada</Tag>;
          default:
            return <Tag color="gold-inverse">{text}</Tag>;
        }
      }
    },
    {
      title: 'FECHA DE INGRESO',
      dataIndex: 'created_at',
      key: 'created_at',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
        <div
          style={{
            padding: '0 8px 8px 8px'
          }}
          onKeyDown={(e) => e.stopPropagation()}>
          <Boton
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
            className="button-close-search-filter-table">
            X
          </Boton>
          <RangePicker
            locale={es}
            placeholder={['Fecha inicial', 'Fecha final']}
            value={selectedKeys[0]}
            onChange={(dates) => {
              setSelectedKeys([dates]);
            }}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Boton
              type="primary"
              onClick={() => {
                confirm();
              }}
              icon={<SearchOutlined />}
              size="small">
              Buscar
            </Boton>
            <Boton onClick={clearFilters} size="small">
              Limpiar
            </Boton>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined
          style={{
            color: filtered ? '#1890ff' : undefined
          }}
        />
      ),
      onFilter: (value, record) => {
        if (!value) return true;
        const start = value[0].startOf('day').toDate(); // Convertir a objeto Date
        const end = value[1].endOf('day').toDate(); // Convertir a objeto Date
        const created = moment(record.created_at, 'DD-MM-YYYY').toDate(); // Convertir a objeto Date
        return created >= start && created <= end;
      },
      sorter: (a, b) => moment(a.created_at, 'DD-MM-YYYY') - moment(b.created_at, 'DD-MM-YYYY'),
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'ACCIONES',
      dataIndex: 'acciones',
      key: 'acciones'
    }
  ];

  const newDataSOL = data?.map((item) => {
    return {
      key: item.id,
      id: item.id,
      name: item?.name,
      status: item?.status,
      created_at: item?.created_at,
      acciones: (
        <Row justify="space-between">
          <Boton
            className="btn-secondary"
            icon={<CheckCircleOutlined />}
            disabled={item.status === '1'}
            onClick={() => handleActivarEmpresa(item.id)}></Boton>
          <Boton
            className="btn-danger"
            danger
            icon={<StopOutlined />}
            disabled={item.status === '0'}
            onClick={() => handleDesactivarEmpresa(item.id)}></Boton>
        </Row>
      )
    };
  });

  return (
    <>
      <Table
        loading={loading}
        columns={tablaReporte}
        dataSource={newDataSOL}
        pagination={{
          position: ['bottomCenter'],
          defaultPageSize: 10,
          pageSizeOptions: [20, 40, 60, 80, 100],
          total: data.total,
          showTotal: (total, range) =>
            `Mostrando empresas del ${range[0]} al ${range[1]} de un total de ${total}`
        }}
        className="mt-4 mb-4"
        locale={localeObjectTable}
        scroll={{
          x: true
        }}
      />
    </>
  );
};

TablaEmpresa.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool,
  setLoading: PropTypes.func
};

export default TablaEmpresa;
